import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';

export default function ShopPanel({ subAccountId }) {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null); // pricing id being checked out
  const [error, setError] = useState(null);

  useEffect(() => {
    apiFetch(`/api/pricing?sub_account_id=${subAccountId}`)
      .then(data => setPricing(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [subAccountId]);

  const handleCheckout = async (pricingId) => {
    setCheckingOut(pricingId);
    try {
      const { url } = await apiFetch('/api/pricing/checkout', {
        method: 'POST',
        body: JSON.stringify({ sub_account_id: subAccountId, pricing_id: pricingId }),
      });
      // Redirect immediately — link expires in 2 minutes
      window.location.href = url;
    } catch (err) {
      alert('Failed to generate checkout link: ' + err.message);
      setCheckingOut(null);
    }
  };

  const formatPrice = (cents) => {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div style={{ marginBottom: '28px' }}>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--slate-900)', marginBottom: '8px' }}>
            Get RideDog GPS Trackers
          </h2>
          <p style={{ color: 'var(--slate-500)', fontSize: '14px', lineHeight: '1.6' }}>
            As a RentFlow Fleet partner you get exclusive discounted pricing on RideDog GPS trackers.
            Track your trailers in real time, get mileage logs, and lock/unlock remotely — all from this app.
          </p>
        </div>

        {/* Features list */}
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px',
          padding: '16px 20px', marginBottom: '28px', display: 'flex', flexWrap: 'wrap', gap: '12px 24px',
        }}>
          {[
            '📍 Live GPS location',
            '📏 Automatic mileage tracking',
            '🔒 Remote lock & unlock',
            '🗺️ Travel path history',
            '⚡ Real-time webhook alerts',
            '🔋 Battery & network status',
          ].map(f => (
            <span key={f} style={{ fontSize: '13px', color: '#166534', fontWeight: '500' }}>{f}</span>
          ))}
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
            padding: '14px 16px', marginBottom: '20px', color: '#dc2626', fontSize: '13px',
          }}>
            {error === 'Account not connected to a GPS provider'
              ? 'Connect your RideDog account first to view partner pricing.'
              : `Could not load pricing: ${error}`}
          </div>
        )}

        {/* Pricing cards */}
        {pricing.length === 0 && !error && (
          <div className="empty-state">
            <span className="empty-icon">🏷️</span>
            <h3>No pricing options available</h3>
            <p>Contact RentFlow support to get access to partner pricing.</p>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          {pricing.map(option => (
            <div key={option.id} style={{
              background: 'white', border: '1px solid var(--slate-200)', borderRadius: '12px',
              padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px',
              boxShadow: 'var(--shadow)',
              transition: 'box-shadow 0.15s',
            }}>
              {/* Badge */}
              <div>
                <span style={{
                  display: 'inline-block',
                  background: option.type === 'lifetime' ? '#fff7ed' : '#f0f9ff',
                  color: option.type === 'lifetime' ? '#ea580c' : '#0284c7',
                  border: `1px solid ${option.type === 'lifetime' ? '#fed7aa' : '#bae6fd'}`,
                  borderRadius: '20px', padding: '2px 10px',
                  fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  {option.type === 'lifetime' ? 'One-Time Purchase' : 'Monthly'}
                </span>
              </div>

              {/* Name & tagline */}
              <div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--slate-900)', marginBottom: '4px' }}>
                  {option.name}
                </div>
                {option.tagline && (
                  <div style={{ fontSize: '13px', color: 'var(--slate-500)' }}>
                    {option.tagline}
                  </div>
                )}
              </div>

              {/* Price */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span style={{ fontSize: '36px', fontWeight: '800', color: 'var(--slate-900)' }}>
                  {formatPrice(option.price)}
                </span>
                {option.type === 'subscription' && (
                  <span style={{ fontSize: '13px', color: 'var(--slate-400)' }}>/month</span>
                )}
              </div>

              {/* What's included */}
              <div style={{ flex: 1 }}>
                {[
                  'Real-time GPS tracking',
                  'Mileage & trip logging',
                  'Remote lock/unlock',
                  'RentFlow Fleet integration',
                ].map(feature => (
                  <div key={feature} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '5px 0', fontSize: '13px', color: 'var(--slate-600)',
                  }}>
                    <span style={{ color: '#16a34a', fontWeight: '700', fontSize: '14px' }}>✓</span>
                    {feature}
                  </div>
                ))}
              </div>

              {/* CTA button */}
              <button
                onClick={() => handleCheckout(option.id)}
                disabled={checkingOut !== null}
                style={{
                  width: '100%', padding: '12px',
                  background: checkingOut === option.id ? 'var(--slate-400)' : 'var(--orange)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '700', cursor: checkingOut !== null ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}>
                {checkingOut === option.id ? 'Redirecting...' : 'Get Started →'}
              </button>
            </div>
          ))}
        </div>

        {/* Fine print */}
        <p style={{ fontSize: '12px', color: 'var(--slate-400)', marginTop: '20px', textAlign: 'center', lineHeight: '1.6' }}>
          You will be redirected to RideDog's secure checkout page to complete your purchase.
          Pricing shown reflects your exclusive RentFlow partner discount.
        </p>
      </div>
    </div>
  );
}
